import '@common/testing/components/TefComsSpidSelect';

import type Vue from 'vue';
import { injectExternalResources } from '@common/lit/injectExternalResources';
import { TemplateTheme } from '@common/lit/TemplateResolver';
import {
  createTestBearerToken, createTestPiranhaToken, isCorrectUrlForWebCrypto, isPiranhaToken,
} from '@common/testing';
import { getBackendUri } from '@common/testing/getBackendUri';
import { config } from '@src/config';
import { TefComs } from '@src/types/typings';

import { components } from './components';

const API_DIRECT_DOMAIN = 'api-4p.high.coms-dev.aws.de.pri.o2.com';

const shortcuts = {
  apiUrlGateway: [
    { value: 'https://ict.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'ict' },
    { value: 'https://e2e2.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'e2e2' },
    { value: 'https://e2e1.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'e2e1' },
    { value: 'https://perf.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'nfr' },
    { value: 'https://prod.high.apigw-prod.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'prod' },
    { value: 'https://api-consent-coms.telefonica.de/gateway/4p-extra-services/v1', label: 'prod/extern' },
  ],
  apiUrlDirect: [
    { value: 'http://localhost:3200', label: 'api4p@localhost' },
    { value: 'http://localhost:8080', label: 'wiremock@localhost' },
    { value: `https://ict-${API_DIRECT_DOMAIN}`, label: 'ict' },
    { value: `https://e2e2-${API_DIRECT_DOMAIN}`, label: 'e2e2' },
    { value: `https://e2e1-${API_DIRECT_DOMAIN}`, label: 'e2e1' },
    { value: `https://nfr-${API_DIRECT_DOMAIN}`, label: 'nfr' },
  ],
  themeBaseUrl: [
    { value: 'https://dev-library.telefonica.de/1', label: 'occl/dev/o2', color: 'text-bg-primary' },
    { value: 'https://dev-library.telefonica.de/2', label: 'occl/dev/blau', color: 'text-bg-primary' },
    { value: 'https://library.telefonica.de/1', label: 'occl/prod/o2', color: 'text-bg-primary' },
    { value: 'https://library.telefonica.de/2', label: 'occl/prod/blau', color: 'text-bg-primary' },
  ],
  tefOne: [
    { value: 'alditalk', label: 'alditalk', color: 'text-bg-secondary' },
    { value: 'ayyildiz', label: 'Ayyildiz', color: 'text-bg-secondary' },
    { value: 'blau', label: 'Blau', color: 'text-bg-secondary' },
    { value: 'fonic', label: 'Fonic', color: 'text-bg-secondary' },
    { value: 'nettokom', label: 'NettoKOM', color: 'text-bg-secondary' },
    { value: 'o2', label: 'o2', color: 'text-bg-secondary' },
    { value: 'ortel', label: 'Ortel', color: 'text-bg-secondary' },
    { value: 'whatsappsim', label: 'WhatsApp SIM', color: 'text-bg-secondary' },
  ],
  tefOneEnv: [
    { value: 'main', label: 'main' },
    { value: 'staging', label: 'staging' },
  ],
  themes: [
    { value: 'occl', label: 'occl', color: 'text-bg-primary' },
    { value: 'tefOne', label: 'tefOne', color: 'text-bg-secondary' },
  ],
};

const environmentMatch = /(^.*)-micro-frontend-proxy/.exec(document.location.host);
if (environmentMatch) {
  const environment = environmentMatch[1];
  const finalApiUrl = `https://${environment}-${API_DIRECT_DOMAIN}`;
  if (!shortcuts.apiUrlDirect.map((u) => u.value).includes(finalApiUrl)) {
    shortcuts.apiUrlDirect.push({ value: finalApiUrl, label: environment });
  }
}

const search = new URLSearchParams(window.location.search);

const currentApiUrl = search.get('apiUrl')
  ?? getBackendUri({ backend: 'api-4p', gatewayContext: '/4p-extra-services{-env}/v1' });

const params = {
  accessToken: search.get('accessToken') ?? '',
  spId: search.get('spId') ?? 'SP901',
  lcId: search.get('lcId') ?? '4711',
  apiUrl: currentApiUrl,
  theme: search.get('theme') ?? 'occl',
  themeBaseUrl: search.get('themeBaseUrl') ?? 'https://dev-library.telefonica.de/1',
  tefOne: search.get('tefOne') ?? 'o2',
  tefOneEnv: search.get('tefOneEnv') ?? 'main',
  traceid: search.get('traceid') ?? '',
  mode: search.get('mode') ?? '',
};

interface CreateMicrofrontendOptions {
  accessToken?: string;
  spId: string;
  lcId: string;
  apiUrl: string;
  theme: string;
  themeBaseUrl?: string;
  tefOne?: string;
  tefOneEnv?: string;
  traceid?: string;
  mode?: string;
}

const createMicroFrontend = async ({
  accessToken = '', spId = '', lcId = '', apiUrl = '', theme = 'occl', themeBaseUrl = 'https://dev-library.telefonica.de/1', traceid = '', mode = '',
}: CreateMicrofrontendOptions) => {
  const mf = document.createElement(config.componentTagName);

  const setAttributes = (attrs: Record<string, string | undefined>) => Object
    .entries(attrs)
    .filter(([, value]) => value) // ignore '', false, undefined
    .forEach(([name, value]) => mf.setAttribute(name, value!));

  if (accessToken === '') accessToken = await createTestBearerToken(); // eslint-disable-line

  setAttributes({
    id: 'consents',
    'access-token': accessToken,
    'lc-id': lcId,
    'api-url': apiUrl,
    theme,
    traceid,
    mode,
  });

  // Do not add empty sp-id or lc-id for piranha test token since
  // that token contains "customer" information with sp-id and lc-id.
  if (spId !== '' && !isPiranhaToken(accessToken)) {
    setAttributes({ 'sp-id': spId });
  }

  if (lcId !== '' && !isPiranhaToken(accessToken)) {
    setAttributes({ 'sp-id': spId });
  }

  if (theme === 'occl') {
    setAttributes({ 'theme-base-url': themeBaseUrl });
  }

  document.getElementById('mf')?.replaceChildren(mf);
};

// @ts-ignore
const app = new Vue({
  el: '#app',

  components,

  data: {
    // definitions
    shortcuts,
    // attributes
    ...params,
    // runtime props
    ready: false,
    info: 'Messages:',
    error: false as boolean | string | Error,
  },

  created() {
    this.init();

    if (this.theme === 'occl') {
    // Load external resources into shadowDOM
      injectExternalResources({
        config,
        parentElement: document.querySelector('head')!,
        theme: this.theme as TemplateTheme,
        themeBaseUrl: this.themeBaseUrl,
      });
    }
  },

  methods: {
    async loadCSS(cssURL: string) {
      return new Promise<void>((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssURL;
        link.onload = () => {
          resolve();
          console.log(`CSS ${cssURL} has loaded!`);
        };
        document.head.appendChild(link);
      });
    },

    async loadTefOne(sp: string, tefOneEnv: string) {
      await this.loadCSS(`https://one.o9.de/${tefOneEnv}/${sp}/variables.css`);
      await this.loadCSS(`https://one.o9.de/${tefOneEnv}/${sp}/components.css`);

      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://one.o9.de/${tefOneEnv}/bundle.js`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load bundle.js'));
        document.body.appendChild(script);
      });

      console.log(`TefOne for ${sp} on ${tefOneEnv} has loaded!`);
    },

    init: () => {
      document.addEventListener(config.componentTagName, (event: Event) => {
        const { detail } = event as TefComs.Event;
        const { name, level, error } = detail ?? {};
        console.log(`[${config.componentTagName}] Received '%s' event with details %o`, name, detail);
        switch (name) {
          case 'load/READY':
            app.ready = true;
            app.setInfo('load/READY: The micro-frontend is ready.');
            break;
          case 'consents/EMPTY':
            app.setInfo('consents/EMPTY: No consents found for this customer');
            break;
          case 'consents/SAVE':
            app.setInfo('consents/SAVE: Consents saved successfully');
            break;
          case 'consents/LOAD':
            app.setInfo('consents/LOAD: Consents successfully loaded');
            break;
          case 'consents/CHANGE':
            app.setInfo('consents/CHANGE: Consent changed');
            break;
          case 'action/CHANGE':
            app.setInfo('action/CHANGE: "Ändern" button pressed');
            break;
          case 'action/CANCEL_MICROFRONTEND':
            app.setInfo('action/CANCEL_MICROFRONTEND: "Zurück" button pressed');
            break;
          default:
        }

        if (level === 'error' || error) {
          app.setError(error ?? detail.message ?? 'an error occurred');
        }
      });
    },

    reload: () => {
      const query = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        query.set(key, app.$data[key] as string ?? '');
      });
      window.location.href = `${window.location.pathname}?${query.toString()}`; // reload page
    },

    create: async () => {
      try {
        if (app.tefOne) {
          await app.loadTefOne(app.tefOne, app.tefOneEnv);
        }
        await createMicroFrontend(app);
      } catch (error) {
        app.setError(error as Error);
      }
    },

    showHtml: () => {
      const escapeHtml = (unsafe: string) => unsafe
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      const html = document.getElementById('consents')?.outerHTML;
      app.setInfo(html ? escapeHtml(html) : 'Element not found.');
    },

    setTestToken: async () => {
      if (!isCorrectUrlForWebCrypto()) {
        app.setError('Creating a token only works within a secure context. Please use "localhost" as a hostname or https to access this page.');
        return;
      }
      app.accessToken = await createTestBearerToken();
      app.setInfo('Created a RAITT token');
    },

    setTestPiranhaToken: async () => {
      if (!isCorrectUrlForWebCrypto()) {
        app.setError('Creating a token only works within a secure context. Please use "localhost" as a hostname or https to access this page.');
        return;
      }

      app.accessToken = await createTestPiranhaToken(app.spId, app.lcId);
      app.setInfo(`Created a piranha token with credentials spId=${app.spId}, lcId=${app.lcId}`);
    },

    setInfo(text: string) {
      app.info = [app.info].concat(text).join('<br />');
      app.error = false;
    },

    setError(error: Error | string) {
      this.error = error;
      if (error instanceof Error) {
        const { message, stack } = error;
        const stackText = stack ? `<br />${stack}` : '';
        this.info = `${message}${stackText}`;
      } else {
        this.info = error;
      }
    },

    setValue<T = unknown>(prop: string, value: T) {
      app.$data[prop] = value;
    },
  },
});

app.create(); // eslint-disable-line
